import moment, { type MomentInput, type Moment as MomentInstance } from "moment";
import Moment, { type MomentProps } from "react-moment";
import { Text } from "./Typography";
type DateProps = {
  date: MomentProps["date"] | null;
  utc?: MomentProps["utc"];
  missing?: string;
  element?: MomentProps["element"];
};
const MomentFormattedText = ({
  date,
  missing,
  format,
  element,
  utc
}: DateProps & {
  format: string;
}) => {
  if (!date) {
    return missing ? <Text>{missing}</Text> : null;
  }
  return <Moment element={element || Text} date={date} format={format} utc={utc} data-sentry-element="Moment" data-sentry-component="MomentFormattedText" data-sentry-source-file="Formatting.tsx" />;
};
export const CalendarText = ({
  date,
  missing,
  element,
  utc
}: DateProps) => {
  const calendar = {
    sameDay: "LT",
    nextDay: "[Tomorrow]",
    nextWeek: "dddd",
    lastDay: "[Yesterday]",
    lastWeek: "[Last] dddd",
    lastYear: "MMM Do",
    sameElse: "MM/DD/YY"
  };
  if (!date) {
    return missing ? <Text>{missing}</Text> : null;
  }
  return <Moment element={element || Text} date={date} calendar={calendar} utc={utc} data-sentry-element="Moment" data-sentry-component="CalendarText" data-sentry-source-file="Formatting.tsx" />;
};
export const CalendarTextSimple = ({
  date,
  missing,
  element
}: DateProps) => {
  const calendar = {
    sameDay: "LT",
    lastWeek: "[Last] dddd",
    lastYear: "MMM Do",
    sameElse: "MM/DD/YY"
  };
  if (!date) {
    return missing ? <Text>{missing}</Text> : null;
  }
  return <Moment element={element || Text} date={date} calendar={calendar} data-sentry-element="Moment" data-sentry-component="CalendarTextSimple" data-sentry-source-file="Formatting.tsx" />;
};
export const DayAndTimeText = (props: DateProps) => {
  return <MomentFormattedText {...props} format="MMM D, YYYY [at] HH:mmA" data-sentry-element="MomentFormattedText" data-sentry-component="DayAndTimeText" data-sentry-source-file="Formatting.tsx" />;
};
export const DateText = ({
  format = "YYYY-MM-DD",
  ...props
}: DateProps & {
  format?: "YYYY-MM-DD" | "MMM D, YYYY" | "Do MMMM YYYY" | "MMMM Do, YYYY";
}) => {
  return <MomentFormattedText {...props} format={format} data-sentry-element="MomentFormattedText" data-sentry-component="DateText" data-sentry-source-file="Formatting.tsx" />;
};
export const CompactDateText = (props: DateProps) => {
  return <MomentFormattedText {...props} format="MM/DD/YY" data-sentry-element="MomentFormattedText" data-sentry-component="CompactDateText" data-sentry-source-file="Formatting.tsx" />;
};
export const TimeText = (props: DateProps) => {
  return <MomentFormattedText {...props} format="LT" data-sentry-element="MomentFormattedText" data-sentry-component="TimeText" data-sentry-source-file="Formatting.tsx" />;
};
export const chatDaySeparator = {
  sameDay: "[Today]",
  lastDay: "[Yesterday]",
  lastWeek: function (this: MomentInstance, m?: MomentInput) {
    const now = moment(m);
    return this.isoWeek() === now.isoWeek() && this.isoWeekYear() === now.isoWeekYear() ? "dddd" : "[Last] dddd";
  },
  // moment's lastWeek bucket covers 2–6 days ago; exactly 7 days ago always
  // lands in the prior ISO week (ISO weeks are aligned 7-day blocks), so
  // keep the "Last dddd" label there instead of falling through to full date.
  sameElse: function (this: MomentInstance, m?: MomentInput) {
    const now = moment(m);
    const diffDays = now.startOf("day").diff(this.clone().startOf("day"), "days");
    return diffDays === 7 ? "[Last] dddd" : "dddd D MMMM";
  }
};
export const ChatDaySeparatorText = ({
  date,
  missing,
  element,
  utc
}: DateProps) => {
  if (!date) {
    return missing ? <Text>{missing}</Text> : null;
  }
  return <Moment element={element || Text} date={date} calendar={chatDaySeparator} utc={utc} data-sentry-element="Moment" data-sentry-component="ChatDaySeparatorText" data-sentry-source-file="Formatting.tsx" />;
};
export const DateTimeText = (props: DateProps) => {
  return <MomentFormattedText {...props} format="YYYY-MM-DD HH:mm" data-sentry-element="MomentFormattedText" data-sentry-component="DateTimeText" data-sentry-source-file="Formatting.tsx" />;
};
export const RelativeTimeText = (props: DateProps) => {
  if (!props.date) {
    return props.missing ? <Text>{props.missing}</Text> : null;
  }
  return <Moment element={Text} date={props.date} fromNow data-sentry-element="Moment" data-sentry-component="RelativeTimeText" data-sentry-source-file="Formatting.tsx" />;
};
type MoneyProps = {
  value?: number | null | undefined;
  currency?: string | null | undefined;
  emptyZero?: boolean;
  missing?: string;
  precision?: number;
};
export const MoneyText = ({
  value,
  currency,
  emptyZero,
  missing,
  precision
}: MoneyProps) => {
  if (value === undefined || value === null || emptyZero && value === 0) {
    return missing ? <Text>{missing}</Text> : null;
  }
  const currencyStr = currency === "USD" ? "$" : currency ?? "$";
  const amount = value.toFixed(precision ?? 2);
  return <Text data-sentry-element="Text" data-sentry-component="MoneyText" data-sentry-source-file="Formatting.tsx">
      {currencyStr}
      {amount}
    </Text>;
};